import { inject, Injectable } from '@angular/core';

import { Comment } from 'app/providers/bridge-api/dashboard.service';
import { ChartDisplayService } from './chart-display.service';
import {
  CommentDataChange,
  commentToAnnotation,
} from './chart-report/chart-report-abstract/chart-report-abstract';

@Injectable({
  providedIn: 'root',
})
export class ChartCommentsService {
  private chartDisplayService = inject(ChartDisplayService);

  constructor() {}

  addComment(
    target: HTMLElement,
    comment: Comment,
    commentsHidden: boolean,
    clickHandlerFn,
    dragHandlerFn
  ) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    for (const series of chart.series) {
      delete series.userOptions.custom?.annotationChanges?.[comment.id];
    }

    const currentAnnotation = chart.annotations.find(
      (a) => a.labels[0].points[0].id === comment.target?.pointId
    );
    if (currentAnnotation) {
      if (
        !comment.titlePinned &&
        !comment.summaryPinned &&
        comment.id !== currentAnnotation.options.id &&
        currentAnnotation.options.draggable
      ) {
        return;
      }

      chart.removeAnnotation(currentAnnotation.options.id);
    }

    const annotation = commentToAnnotation(comment);
    annotation.visible = !commentsHidden;
    annotation.events = {
      click: clickHandlerFn,
      afterUpdate: (e) => dragHandlerFn(e),
    };

    chart.addAnnotation(annotation);
    // Immediately center donut annotations if the chart exposes the helper
    try {
      // Call synchronously and after paint to cover both cases
      chart.centerDonutAnnotations?.();
      requestAnimationFrame(() => chart.centerDonutAnnotations?.());
      setTimeout(() => chart.centerDonutAnnotations?.(), 0);
    } catch (_) {}
  }

  removeComment(target: HTMLElement, comment: Comment): boolean {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    const isAnnotationPresent = chart.annotations.some((a) => a.options.id === comment.id);

    chart.removeAnnotation(comment.id);
    for (const series of chart.series) {
      delete series.userOptions.custom?.annotationChanges?.[comment.id];
    }

    return isAnnotationPresent;
  }

  editComment(
    target: HTMLElement,
    comment: Comment,
    commentsHidden: boolean,
    clickHandlerFn,
    dragHandlerFn
  ) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    if (this.isCommentVisible(target, comment)) {
      this.addComment(target, comment, commentsHidden, clickHandlerFn, dragHandlerFn);
    }
  }

  isCommentVisible(target: HTMLElement, comment: Comment) {
    return this.getVisibleComments(target, [comment]).includes(comment);
  }

  getVisibleComments(target: HTMLElement, comments: Comment[]) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return [];
    }

    return this.getVisibleAnnotations(target).flatMap((a) => {
      return comments.filter(
        (c) =>
          c.target.pointId === a.labels[0]?.points[0]?.id &&
          c.payload.widgetDataSourceId === a.userOptions.custom.dataSourceId
      );
    });
  }

  private getVisibleAnnotations(target: HTMLElement) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return [];
    }

    return chart.annotations.filter(
      (a) => a.labels[0]?.points[0]?.series.visible && a.labels[0]?.points[0]?.isInside !== false
    );
  }

  getCommentChangeForComment(target: HTMLElement, comment: Partial<Comment>): CommentDataChange {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    const annotation = chart.annotations.find(
      (a) => a.options.id === comment.id || a.labels[0].points[0].id === comment.target?.pointId
    );
    return annotation?.labels[0].points[0].series.userOptions.custom?.annotationChanges?.[
      comment.id
    ];
  }

  hideOrShowComments(target: HTMLElement, commentsHidden: boolean) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart?.annotations) {
      return;
    }

    chart.annotations.forEach((a) => {
      a.options.visible = !commentsHidden;
      if (!a.options.visible) {
        a.graphic.hide();
      } else {
        a.graphic.show();
      }
    });
  }

  setAnnotationSelected(target: HTMLElement, targetPoint: any) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    chart.annotations.forEach((ann) => {
      const annotationIconClass = ann.userOptions.draggable ? '.comment-card-chart' : '.kxi-icon';
      const classList = ann.graphic.div?.querySelector(annotationIconClass)?.classList;
      classList?.remove('selected');

      if (!targetPoint || ann.labels[0]?.points[0]?.id !== targetPoint) {
        return;
      }

      classList?.add('selected');
    });
  }
}
